import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { twoFactorDisable } from '@/api/two-factor-auth';
import { toast_error, toast_success } from '@/libs/toast-wrappers';
import { otpSchema } from '@/utils/zod-schemas';

import { BasicDrawer } from '@/components/ui/basic-drawer';

import { Button } from '../ui/button';
import { InputOTP, InputOTPSlotGroup } from '../ui/input-otp';
import { Label } from '../form/label';

const schema = z.object({ verification_code: otpSchema });
type DisableFormData = z.infer<typeof schema>;

export function TwoFactorAuthDisableForm() {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { onFormClose } = useOutletContext<{
    onFormClose: () => Promise<void>;
  }>();

  const navigate = useNavigate();

  const { control, handleSubmit } = useForm<DisableFormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<DisableFormData> = async (data) => {
    setIsLoading(true);
    try {
      const response = await twoFactorDisable(data.verification_code);
      if (!response.enabled) {
        await onFormClose();
        toast_success(t('two-factor-auth-disabled-success'));
        navigate(-1);
      }
    } catch {
      toast_error(t('two-factor-auth-code-error'));
      setIsLoading(false);
    }
  };

  const onClose = () => {
    navigate(-1);
  };

  return (
    <BasicDrawer size="sm" open={true} onClose={onClose} title={t('two-factor-auth-disable-title')}>
      <div className="flex flex-col gap-4 justify-center items-center ">
        <form
          className="flex items-center max-w-72 flex-col gap-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Label>{t('two-factor-auth-verify-description')}</Label>
          <Controller
            control={control}
            name="verification_code"
            render={({ field }) => (
              <InputOTP maxLength={6} {...field}>
                <InputOTPSlotGroup />
              </InputOTP>
            )}
          />
          <Button
            disabled={isLoading}
            loading={isLoading}
            type="submit"
            className="w-full inline-block"
          >
            {t('two-factor-auth-disable-button')}
          </Button>
        </form>
      </div>
    </BasicDrawer>
  );
}
